<script lang="ts">
	import { yyyy, zzzz } from "../../../store.svelte";
	import { toast } from "../../../lib/toast.svelte.ts";

	let { analysisName = "" } = $props();

	// CSV state
	let csvFile: File | null = $state(null);
	let headers: string[] = $state([]);
	let rows: string[][] = $state([]);
	let error: string = $state("");
	let isLoading: boolean = $state(false);

	// API state
	let isSubmitting: boolean = $state(false);
	let result: any = $state(null);

	// Column mapping state
	let tableColumn: string = $state("");
	let pkidColumn: string = $state("");
	let energyColumn: string = $state("");

	// Common patterns for auto-detection
	const tablePatterns = ["table", "lw_table", "device_type", "type", "asset_type"];
	const pkidPatterns = ["pkid", "lw_table_pkid", "id", "primary_key", "pk", "asset_id"];
	const energyPatterns = ["energy", "energy_value", "kwh", "kwh_consumed", "consumption", "usage", "value"];

	function autoDetectColumn(headers: string[], patterns: string[]): string {
		const lowerHeaders = headers.map(h => h.toLowerCase());
		for (const pattern of patterns) {
			const index = lowerHeaders.findIndex(h => h === pattern || h.includes(pattern));
			if (index !== -1) {
				return headers[index];
			}
		}
		return "";
	}

	function parseCSV(text: string): { headers: string[]; rows: string[][] } {
		const lines = text.trim().split(/\r?\n/);
		if (lines.length < 2) {
			throw new Error("CSV must have a header row and at least one data row");
		}

		const parseRow = (line: string): string[] => {
			const result: string[] = [];
			let current = "";
			let inQuotes = false;

			for (let i = 0; i < line.length; i++) {
				const char = line[i];
				if (char === '"') {
					if (inQuotes && line[i + 1] === '"') {
						current += '"';
						i++;
					} else {
						inQuotes = !inQuotes;
					}
				} else if (char === ',' && !inQuotes) {
					result.push(current.trim());
					current = "";
				} else {
					current += char;
				}
			}
			result.push(current.trim());
			return result;
		};

		const headers = parseRow(lines[0]);
		const rows = lines.slice(1).filter(line => line.trim()).map(parseRow);

		return { headers, rows };
	}

	function handleFileSelect(event: Event) {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];

		if (!file) {
			return;
		}

		if (!file.name.toLowerCase().endsWith('.csv')) {
			error = "Please select a CSV file";
			return;
		}

		csvFile = file;
		error = "";
		isLoading = true;

		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const text = e.target?.result as string;
				const parsed = parseCSV(text);
				headers = parsed.headers;
				rows = parsed.rows;

				// Auto-detect columns
				tableColumn = autoDetectColumn(headers, tablePatterns);
				pkidColumn = autoDetectColumn(headers, pkidPatterns);
				energyColumn = autoDetectColumn(headers, energyPatterns);

				isLoading = false;
			} catch (err) {
				error = err instanceof Error ? err.message : "Failed to parse CSV";
				headers = [];
				rows = [];
				isLoading = false;
			}
		};

		reader.onerror = () => {
			error = "Failed to read file";
			isLoading = false;
		};

		reader.readAsText(file);
	}

	function clearFile() {
		csvFile = null;
		headers = [];
		rows = [];
		error = "";
		tableColumn = "";
		pkidColumn = "";
		energyColumn = "";
	}

	// Validation
	let validationError = $derived.by(() => {
		if (!energyColumn || rows.length === 0) return "";

		const energyIndex = headers.indexOf(energyColumn);
		if (energyIndex === -1) return "";

		const invalidRows: number[] = [];
		for (let i = 0; i < rows.length; i++) {
			const value = rows[i][energyIndex];
			if (value && isNaN(parseFloat(value))) {
				invalidRows.push(i + 2); // +2 for header row and 1-based index
			}
		}

		if (invalidRows.length > 0) {
			const shown = invalidRows.slice(0, 5);
			const more = invalidRows.length > 5 ? ` and ${invalidRows.length - 5} more` : "";
			return `Energy column contains non-numeric values in rows: ${shown.join(", ")}${more}`;
		}
		return "";
	});

	let isValid = $derived(
		analysisName.length >= 5 &&
		analysisName.length <= 50 &&
		tableColumn !== "" &&
		pkidColumn !== "" &&
		energyColumn !== "" &&
		!validationError &&
		rows.length > 0
	);

	// Get column index helper
	function getColumnIndex(columnName: string): number {
		return headers.indexOf(columnName);
	}

	// Prepare mapped data for submission
	function getMappedData() {
		const tableIdx = getColumnIndex(tableColumn);
		const pkidIdx = getColumnIndex(pkidColumn);
		const energyIdx = getColumnIndex(energyColumn);

		return rows.map(row => ({
			lw_table: row[tableIdx],
			lw_table_pkid: row[pkidIdx],
			energy_value: parseFloat(row[energyIdx])
		}));
	}

	// Start analysis handler
	async function startAnalysis() {
		if (!isValid || isSubmitting) return;

		isSubmitting = true;
		result = null;

		const data = getMappedData();
		const url = yyyy.uri.api;

		try {
			const response = await fetch(`${url}/rpc/calculate_energy_balance`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + yyyy.sesh.logtoken.token,
				},
				body: JSON.stringify({
					wireident: yyyy.sesh.activeLivewire.wireident,
					eb_name: analysisName,
					eb_data: data,
				}),
			});

			if (response.ok) {
				result = await response.json();
				console.log("Energy balance result:", result);
				zzzz.emit("showEnergyBalance", result);
				toast.success(`Analysis complete: ${result.features?.length || 0} features`);
			} else {
				const errorText = await response.text();
				toast.error(`API Error: ${response.status} - ${errorText}`);
				console.error("API error:", errorText);
			}
		} catch (err) {
			const errorMsg = err instanceof Error ? err.message : "Failed to submit analysis";
			toast.error(errorMsg);
			console.error("Submit error:", err);
		} finally {
			isSubmitting = false;
		}
	}

	// Preview rows (first 10)
	let previewRows = $derived(rows.slice(0, 10));
</script>

{#if result}
	<div class="results-section">
		<h5 class="section-title">Energy Balance Results</h5>
		<p class="results-summary">{result.features?.length || 0} features on map</p>

		<div class="results-table-container">
			<table class="results-table">
				<thead>
					<tr>
						<th>Table</th>
						<th>PKID</th>
						<th class="num">Energy</th>
						<th class="num">DS Total</th>
						<th class="num">Loss %</th>
					</tr>
				</thead>
				<tbody>
					{#each result.features as feature}
						{@const props = feature.properties}
						{@const lossPercent = ((props.energy_value - props.ds_total) / props.energy_value * 100).toFixed(1)}
						<tr>
							<td>{props.lw_table}</td>
							<td>{props.lw_table_pkid}</td>
							<td class="num">{props.energy_value.toLocaleString()}</td>
							<td class="num">{props.ds_total.toLocaleString()}</td>
							<td class="num loss" class:high-loss={parseFloat(lossPercent) > 10}>{lossPercent}%</td>
						</tr>
					{/each}
				</tbody>
			</table>
		</div>
	</div>
{:else}
	<div class="upload-section">
		<h5 class="section-title">Upload CSV Data</h5>

		{#if !csvFile}
			<div class="file-input-container">
				<label class="file-label">
					<input
						type="file"
						accept=".csv"
						onchange={handleFileSelect}
						class="file-input"
					/>
					<span class="file-cta">
						<span class="file-icon">+</span>
						<span class="file-text">Select CSV File</span>
					</span>
				</label>
			</div>
		{:else}
			<div class="file-info">
				<span class="file-name">{csvFile.name}</span>
				<button class="clear-btn" onclick={clearFile}>Clear</button>
			</div>
		{/if}

		{#if error}
			<div class="error-message">{error}</div>
		{/if}

		{#if isLoading}
			<div class="loading">
				<div class="spinner"></div>
				<span>Parsing CSV...</span>
			</div>
		{/if}

		{#if headers.length > 0 && !isLoading}
			<div class="row-count">
				<strong>{rows.length}</strong> rows loaded
			</div>

			<div class="mapping-section">
				<h5 class="section-title">Column Mapping</h5>
				<p class="mapping-hint">Map your CSV columns to the required fields:</p>

				<div class="mapping-field">
					<label for="table-col">Table Name</label>
					<select id="table-col" bind:value={tableColumn}>
						<option value="">-- Select column --</option>
						{#each headers as header}
							<option value={header}>{header}</option>
						{/each}
					</select>
				</div>

				<div class="mapping-field">
					<label for="pkid-col">Primary Key</label>
					<select id="pkid-col" bind:value={pkidColumn}>
						<option value="">-- Select column --</option>
						{#each headers as header}
							<option value={header}>{header}</option>
						{/each}
					</select>
				</div>

				<div class="mapping-field">
					<label for="energy-col">Energy Value</label>
					<select id="energy-col" bind:value={energyColumn}>
						<option value="">-- Select column --</option>
						{#each headers as header}
							<option value={header}>{header}</option>
						{/each}
					</select>
				</div>

				{#if validationError}
					<div class="validation-error">{validationError}</div>
				{/if}
			</div>

			<div class="preview-section">
				<h5 class="section-title">Preview</h5>
				<div class="preview-table-container">
					<table class="preview-table">
						<thead>
							<tr>
								{#each headers as header}
									<th
										class:mapped={header === tableColumn || header === pkidColumn || header === energyColumn}
										class:table-col={header === tableColumn}
										class:pkid-col={header === pkidColumn}
										class:energy-col={header === energyColumn}
									>
										{header}
										{#if header === tableColumn}
											<span class="col-badge">Table</span>
										{:else if header === pkidColumn}
											<span class="col-badge">PK</span>
										{:else if header === energyColumn}
											<span class="col-badge">Energy</span>
										{/if}
									</th>
								{/each}
							</tr>
						</thead>
						<tbody>
							{#each previewRows as row}
								<tr>
									{#each headers as header, i}
										<td
											class:mapped={header === tableColumn || header === pkidColumn || header === energyColumn}
										>
											{row[i] || ""}
										</td>
									{/each}
								</tr>
							{/each}
						</tbody>
					</table>
				</div>
				{#if rows.length > 10}
					<p class="preview-note">Showing first 10 of {rows.length} rows</p>
				{/if}
			</div>

			{#if isValid}
				<button class="start-btn" onclick={startAnalysis} disabled={isSubmitting}>
					{#if isSubmitting}
						<span class="btn-spinner"></span>
						Analyzing...
					{:else}
						Start Analysis
					{/if}
				</button>
			{/if}
		{/if}
	</div>
{/if}

<style>
	.upload-section {
		margin-top: 1rem;
	}

	.section-title {
		font-size: 0.95rem;
		font-weight: 600;
		color: #363636;
		margin-bottom: 0.75rem;
	}

	.file-input-container {
		margin-bottom: 1rem;
	}

	.file-label {
		display: block;
		cursor: pointer;
	}

	.file-input {
		display: none;
	}

	.file-cta {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1rem;
		border: 2px dashed #dbdbdb;
		border-radius: 6px;
		background: #fafafa;
		transition: all 0.2s;
	}

	.file-cta:hover {
		border-color: #ff3e00;
		background: rgba(255, 62, 0, 0.05);
	}

	.file-icon {
		font-size: 1.5rem;
		margin-right: 0.5rem;
		color: #ff3e00;
	}

	.file-text {
		color: #4a5568;
		font-weight: 500;
	}

	.file-info {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0.75rem;
		background: #f5f5f5;
		border-radius: 4px;
		margin-bottom: 1rem;
	}

	.file-name {
		font-weight: 500;
		color: #363636;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.clear-btn {
		padding: 0.25rem 0.75rem;
		background: #ff3e00;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-size: 0.85rem;
	}

	.clear-btn:hover {
		background: #e63600;
	}

	.error-message {
		padding: 0.75rem;
		background: #fff5f5;
		border: 1px solid #feb2b2;
		border-radius: 4px;
		color: #c53030;
		font-size: 0.9rem;
		margin-bottom: 1rem;
	}

	.validation-error {
		padding: 0.5rem 0.75rem;
		background: #fffaf0;
		border: 1px solid #fbd38d;
		border-radius: 4px;
		color: #c05621;
		font-size: 0.85rem;
		margin-top: 0.75rem;
	}

	.loading {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 0.75rem;
		padding: 1rem;
		color: #4a5568;
	}

	.spinner {
		width: 20px;
		height: 20px;
		border: 2px solid rgba(255, 62, 0, 0.1);
		border-top-color: #ff3e00;
		border-radius: 50%;
		animation: spin 0.8s linear infinite;
	}

	@keyframes spin {
		to { transform: rotate(360deg); }
	}

	.row-count {
		padding: 0.5rem 0.75rem;
		background: #f0fff4;
		border: 1px solid #9ae6b4;
		border-radius: 4px;
		color: #276749;
		font-size: 0.9rem;
		margin-bottom: 1rem;
	}

	.mapping-section {
		margin-bottom: 1rem;
		padding: 1rem;
		background: #f9fafb;
		border-radius: 6px;
	}

	.mapping-hint {
		font-size: 0.85rem;
		color: #718096;
		margin-bottom: 0.75rem;
	}

	.mapping-field {
		margin-bottom: 0.75rem;
	}

	.mapping-field:last-of-type {
		margin-bottom: 0;
	}

	.mapping-field label {
		display: block;
		font-size: 0.85rem;
		font-weight: 500;
		color: #4a5568;
		margin-bottom: 0.25rem;
	}

	.mapping-field select {
		width: 100%;
		padding: 0.5rem;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		font-size: 0.9rem;
		background: white;
	}

	.mapping-field select:focus {
		outline: none;
		border-color: #ff3e00;
	}

	.preview-section {
		margin-bottom: 1rem;
	}

	.preview-table-container {
		overflow-x: auto;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
	}

	.preview-table {
		width: 100%;
		border-collapse: collapse;
		font-size: 0.8rem;
	}

	.preview-table th,
	.preview-table td {
		padding: 0.5rem;
		text-align: left;
		border-bottom: 1px solid #eee;
		white-space: nowrap;
	}

	.preview-table th {
		background: #f5f5f5;
		font-weight: 600;
		color: #363636;
		position: relative;
	}

	.preview-table th.mapped {
		background: rgba(255, 62, 0, 0.1);
	}

	.preview-table td.mapped {
		background: rgba(255, 62, 0, 0.05);
	}

	.col-badge {
		display: inline-block;
		padding: 0.1rem 0.3rem;
		background: #ff3e00;
		color: white;
		font-size: 0.65rem;
		border-radius: 3px;
		margin-left: 0.25rem;
		vertical-align: middle;
	}

	.preview-table tbody tr:hover {
		background: #fafafa;
	}

	.preview-note {
		font-size: 0.8rem;
		color: #718096;
		text-align: center;
		margin-top: 0.5rem;
	}

	.start-btn {
		width: 100%;
		padding: 0.75rem 1rem;
		background: #ff3e00;
		color: white;
		border: none;
		border-radius: 4px;
		font-size: 1rem;
		font-weight: 600;
		cursor: pointer;
		transition: background 0.2s;
	}

	.start-btn:hover {
		background: #e63600;
	}

	.start-btn:active {
		background: #cc3300;
	}

	.start-btn:disabled {
		background: #999;
		cursor: not-allowed;
	}

	.start-btn:disabled:hover {
		background: #999;
	}

	.btn-spinner {
		display: inline-block;
		width: 14px;
		height: 14px;
		border: 2px solid rgba(255, 255, 255, 0.3);
		border-top-color: white;
		border-radius: 50%;
		animation: spin 0.8s linear infinite;
		margin-right: 0.5rem;
		vertical-align: middle;
	}

	.results-section {
		margin-top: 1rem;
	}

	.results-summary {
		font-size: 0.9rem;
		color: #276749;
		background: #f0fff4;
		padding: 0.5rem 0.75rem;
		border-radius: 4px;
		margin-bottom: 1rem;
		text-align: center;
	}

	.results-table-container {
		overflow-x: auto;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
	}

	.results-table {
		width: 100%;
		border-collapse: collapse;
		font-size: 0.8rem;
	}

	.results-table th,
	.results-table td {
		padding: 0.5rem;
		text-align: left;
		border-bottom: 1px solid #eee;
	}

	.results-table th {
		background: #f5f5f5;
		font-weight: 600;
		color: #363636;
		position: sticky;
		top: 0;
	}

	.results-table th.num,
	.results-table td.num {
		text-align: right;
	}

	.results-table tbody tr:hover {
		background: #fafafa;
	}

	.results-table td.loss {
		font-weight: 600;
		color: #276749;
	}

	.results-table td.loss.high-loss {
		color: #c53030;
	}
</style>
